import { FetchOptionsCallback } from 'global';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import {
  CreateCloseReasonVariables,
  MutateCloseReasonsResponse,
  DeleteCloseReasonVariables,
  CreateCloseReasonsResponse,
  CreateCloseReasonsVariables,
  UpdateCloseReasonLabelVariables,
  UpdateCloseReasonStatusVariables,
} from './types';
import { bpfAPI } from '../index';

export function useBPFCloseReasonsMutations() {
  const { showNotification } = useNotifications();

  const [onDeleteCloseReason, { loading: isDeleting }] = useMutation<
    MutateCloseReasonsResponse,
    DeleteCloseReasonVariables
  >(bpfAPI.deleteCloseReason);

  const [onCreateCloseReasons] = useMutation<CreateCloseReasonsResponse, CreateCloseReasonsVariables>(
    bpfAPI.createCloseReasons
  );

  const [onCreateCloseReason, { loading: isCreating }] = useMutation<
    MutateCloseReasonsResponse,
    CreateCloseReasonVariables
  >(bpfAPI.createCloseReason);

  const [onUpdateCloseReasonLabel, { loading: isLabelUpdating }] = useMutation<
    MutateCloseReasonsResponse,
    UpdateCloseReasonLabelVariables
  >(bpfAPI.updateCloseReasonLabel);

  const [onUpdateCloseReasonStatus, { loading: isStatusUpdating }] = useMutation<
    MutateCloseReasonsResponse,
    UpdateCloseReasonStatusVariables
  >(bpfAPI.updateCloseReasonStatus);

  const handleCreateCloseReasons = async (
    dto: CreateCloseReasonsVariables['input'],
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, CreateCloseReasonsResponse, CreateCloseReasonsVariables>
  ) => {
    try {
      const { data, errors } = await onCreateCloseReasons({
        variables: { input: dto },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.createCloseReasons?.error || !data?.bpf?.createCloseReasons?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.createCloseReasons?.error ?? 'Something went wrong', { type: 'error' });
        return [];
      }

      showNotification('Reasons created successfully.');

      return data.bpf.createCloseReasons.entities;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return [];
    }
  };

  const handleCreateCloseReason = async (
    dto: CreateCloseReasonVariables['input'],
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, MutateCloseReasonsResponse, CreateCloseReasonVariables>
  ) => {
    try {
      const { data, errors } = await onCreateCloseReason({
        variables: { input: dto },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return [];
      }

      showNotification('Reason created successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleUpdateCloseReasonStatus = async (
    id: string,
    status: boolean,
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, MutateCloseReasonsResponse, UpdateCloseReasonStatusVariables>
  ) => {
    try {
      const { data, errors } = await onUpdateCloseReasonStatus({
        variables: { id, status },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return [];
      }

      showNotification('Close reason updated successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleUpdateCloseReasonLabel = async (
    id: string,
    label: string,
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, MutateCloseReasonsResponse, UpdateCloseReasonLabelVariables>
  ) => {
    try {
      const { data, errors } = await onUpdateCloseReasonLabel({
        variables: { id, label },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleDeleteCloseReason = async (
    id: string,
    fetchOptions?: FetchOptionsCallback<typeof bpfAPI, MutateCloseReasonsResponse, DeleteCloseReasonVariables>
  ) => {
    try {
      const { data, errors } = await onDeleteCloseReason({
        variables: { input: { id } },
        ...(typeof fetchOptions === 'function' ? fetchOptions(bpfAPI) : {}),
      });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Close reason deleted successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  return {
    loading: {
      isCreating,
      isDeleting,
      isLabelUpdating,
      isStatusUpdating,
    },
    actions: {
      handleCreateCloseReason,
      handleDeleteCloseReason,
      handleCreateCloseReasons,
      handleUpdateCloseReasonLabel,
      handleUpdateCloseReasonStatus,
    },
  };
}
